<script lang="ts">
	/**
	 * The money over time (Design §13).
	 *
	 * **One currency at a time, chosen rather than merged.** Every other panel on this page renders
	 * per-currency figures side by side, which a chart cannot do honestly: two currencies on one
	 * axis are two scales, and a line drawn against the wrong one is a shape that is confidently
	 * wrong. So the currency is a control, defaulting to whichever the store took most in, and a
	 * store with one currency never sees the control at all.
	 *
	 * **What is drawn is a choice too**, and it is the one the page is actually read for: the net
	 * per bucket, or one kind of movement on its own. Netting by default is deliberate — a revenue
	 * line that ignores refunds is the figure a store quotes and then cannot reconcile against its
	 * bank.
	 *
	 * The table under the chart is not a fallback. It is the relief the palette's light-mode
	 * contrast obliges, and it is also what somebody copies into a spreadsheet.
	 */

	import { Table, TableBody, TableBodyCell, TableBodyRow, TableHead, TableHeadCell } from 'flowbite-svelte';
	import { EmptyState, Panel, SelectField, TrendChart } from '../../../components';
	import { formatAmount } from '../../../stores/storefront';
	import { getReportTimeseries } from '../../../AppNetwork';
	import type {
		AnalyticsSeries,
		ReportEventKind,
		ReportQuery,
		ReportTimeseries
	} from '../../../AppTypes';

	export let filter: ReportQuery;

	/** `''` is the net of everything, which is what the page is opened to answer. */
	const VIEWS: { value: '' | ReportEventKind; name: string }[] = [
		{ value: '', name: 'Net of everything' },
		{ value: 'sale', name: 'Sales' },
		{ value: 'renewal', name: 'Renewals' },
		{ value: 'adjustment', name: 'Adjustments' },
		{ value: 'refund', name: 'Refunds' },
		{ value: 'chargeback', name: 'Chargebacks' }
	];

	let view: '' | ReportEventKind = '';
	let currency: string = '';
	let data: ReportTimeseries | null = null;
	let error = '';
	let loading = false;

	/**
	 * Re-read whenever the filter moves.
	 *
	 * Keyed on a **string**, not on the object: a reactive statement reading `filter` directly
	 * re-runs on this panel's own writes and fetches for ever, which is the same trap the product
	 * page's `quoteKey` exists to avoid. The view and the currency are *not* in the key — both are
	 * applied to data already in hand, so changing either must not cost a round trip.
	 */
	$: queryKey = JSON.stringify(filter);
	$: void fnLoad(queryKey);

	async function fnLoad(_key: string): Promise<void> {
		loading = true;
		try {
			data = await getReportTimeseries(filter);
			error = '';
		} catch (caught) {
			error = caught instanceof Error ? caught.message : 'Failed to load the trend';
			data = null;
		} finally {
			loading = false;
		}
	}

	/** Every currency the window holds, in the order the ledger returned them. */
	$: currencies = data === null ? [] : [...new Set(data.points.map(point => point.currency))].sort();

	/**
	 * The currency being drawn, corrected whenever the data no longer contains the chosen one.
	 *
	 * Without the correction, narrowing the filter to a range with no euro sales leaves the control
	 * reading EUR over an empty chart — which reads as a month with no sales rather than as a
	 * currency with none.
	 */
	$: if (currencies.length > 0 && !currencies.includes(currency)) {
		currency = fnBusiest(currencies);
	}

	function fnBusiest(available: string[]): string {
		if (data === null) return available[0];
		const totals = new Map<string, number>();
		for (const point of data.points) {
			totals.set(point.currency, (totals.get(point.currency) ?? 0) + Math.abs(point.amount));
		}
		return [...available].sort((a, b) => (totals.get(b) ?? 0) - (totals.get(a) ?? 0))[0];
	}

	/**
	 * The series, with **every bucket present** — the empty ones included.
	 *
	 * The server supplies the axis rather than the points supplying it, because a chart drawn from
	 * the rows alone silently closes its own gaps: a fortnight with no sales renders as the day
	 * before joined to the day after, which reads as a flat line rather than as nothing having
	 * happened.
	 */
	$: series = ((): AnalyticsSeries[] => {
		if (data === null || currency === '') return [];
		const byBucket = new Map<string, number>();
		for (const point of data.points) {
			if (point.currency !== currency) continue;
			if (view !== '' && point.kind !== view) continue;
			byBucket.set(point.bucket, (byBucket.get(point.bucket) ?? 0) + point.amount);
		}
		return [
			{
				currency,
				points: data.buckets.map(bucket => ({ bucket, value: byBucket.get(bucket) ?? 0 }))
			}
		];
	})();

	$: title = VIEWS.find(entry => entry.value === view)?.name ?? 'Net of everything';

	function fnBucket(bucket: string): string {
		const date = new Date(bucket);
		return Number.isNaN(date.getTime()) ? bucket : date.toISOString().slice(0, 10);
	}

	function fnMoney(amount: number): string {
		return $formatAmount(amount, currency) ?? `${amount} minor units`;
	}
</script>

<Panel title="Over time" faIcon="chart-area">
	<svelte:fragment slot="titleRight">
		<!-- Labelled for the reason every other control on this page is: a header dropdown reading
		     "Refunds" says nothing about what else it could show. -->
		<label class="text-xs text-default shrink-0" for="reports-trend-view">Showing</label>
		<SelectField id="reports-trend-view" size="sm" class="w-44" bind:value={view} items={VIEWS} />
		{#if currencies.length > 1}
			<label class="text-xs text-default shrink-0" for="reports-trend-currency">in</label>
			<SelectField
				id="reports-trend-currency"
				size="sm"
				class="w-24"
				bind:value={currency}
				items={currencies.map(code => ({ value: code, name: code.toUpperCase() }))}
			/>
		{/if}
	</svelte:fragment>

	{#if error.length > 0}
		<EmptyState variant="error" title="The trend could not be loaded." description={error} />
	{:else if data === null}
		<EmptyState variant="loading" />
	{:else if series.length === 0 || data.points.length === 0}
		<EmptyState variant="empty" title="Nothing moved in this range." />
	{:else}
		{#if currencies.length > 1}
			<!-- Stated, because a chart of one currency inside a store selling in three is a chart of
			     part of the business, and the axis label alone does not say so. -->
			<p class="text-default text-xs mb-2">
				Drawn in {currency.toUpperCase()} only. Two currencies on one axis are two scales.
			</p>
		{/if}

		<div class:opacity-50={loading}>
			<TrendChart {series} {title} isMoney granularity={data.granularity} />
		</div>

		<div class="overflow-x-auto mt-3">
			<Table class="dataTable">
				<TableHead>
					<TableHeadCell>Bucket</TableHeadCell>
					<TableHeadCell class="text-right">
						{title} ({currency.toUpperCase()})
					</TableHeadCell>
				</TableHead>
				<TableBody>
					{#each series[0].points as point (point.bucket)}
						<TableBodyRow>
							<TableBodyCell class="dataTable">{fnBucket(point.bucket)}</TableBodyCell>
							<TableBodyCell class="dataTable text-right tabular-nums">
								{fnMoney(point.value)}
							</TableBodyCell>
						</TableBodyRow>
					{/each}
				</TableBody>
			</Table>
		</div>
	{/if}
</Panel>
